/**
 * The role dialog's Notifications section (docs/notifications/
 * implementation-guide.md 8.2): which notifications the dialog OFFERS for the
 * claims currently ticked, which notifications are ticked for the
 * administrator when a claim is ticked, and what happens to the ticked
 * notifications when a claim is unticked.
 *
 * The rule is the server's (authority/catalogue.js ROLE_SAVE, and 0024's
 * trigger): a role may subscribe to a notification only when it holds one of
 * the notification's required claims, and never to one sent only to the
 * person it is about. This module is the page's copy of that rule, so the
 * dialog never offers a box the server would refuse. Pure: no React, so
 * roleNotifications.test.js runs it in node.
 *
 * A catalogue entry is what GET /access/notifications returns:
 *   { code, group, label, help, severity, requiredClaims, subscribable, forceEmail, emailLocked, roles }
 */

/** Whether the ticked claims cover one entry: subscribable, and one of its required claims is ticked. */
export function isOffered(entry, claims) {
  if (!entry?.subscribable) return false;
  const held = new Set(claims ?? []);
  return (entry.requiredClaims ?? []).some((claim) => held.has(claim));
}

/**
 * The entries to show for the ticked claims, grouped by Console section in
 * the catalogue's order - the same grouping the Permissions list above uses.
 *
 * @returns {Array<[string, object[]]>} [group, entries] pairs
 */
export function offeredByGroup(catalogue, claims) {
  const groups = new Map();
  for (const entry of catalogue ?? []) {
    if (!isOffered(entry, claims)) continue;
    const group = entry.group ?? entry.screen ?? 'Other';
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(entry);
  }
  return [...groups.entries()];
}

/**
 * The ticked notifications that survive a change of claims: anything the new
 * claims no longer cover is unticked, as saveRole would drop it. Returned
 * sorted, as the server stores the set.
 */
export function keepCovered(selected, catalogue, claims) {
  const byCode = new Map((catalogue ?? []).map((entry) => [entry.code, entry]));
  return (selected ?? []).filter((code) => isOffered(byCode.get(code), claims)).sort();
}

/**
 * The ticked notifications after one or more claims were newly ticked: every
 * subscribable notification that one of the NEWLY ticked claims covers is
 * ticked for the administrator, so ticking a permission brings its related
 * notifications with it. Only the claims that were just added count - a claim
 * that was already ticked does not re-tick a notification the administrator
 * deliberately unticked, and unticking a notification afterwards still sticks.
 *
 * Unticking is keepCovered's job; this only ever adds. Returned sorted, as the
 * server stores the set.
 *
 * @param {string[]} selected   the notifications ticked before the change
 * @param {object[]} catalogue  GET /access/notifications entries
 * @param {string[]} prevClaims the claims ticked before the change
 * @param {string[]} nextClaims the claims ticked after the change
 */
export function addNewlyCovered(selected, catalogue, prevClaims, nextClaims) {
  const before = new Set(prevClaims ?? []);
  // The claims this change ticked; none means nothing new to bring in.
  const added = (nextClaims ?? []).filter((claim) => !before.has(claim));
  const result = new Set(selected ?? []);
  if (added.length === 0) return [...result].sort();
  for (const entry of catalogue ?? []) {
    // isOffered with only the added claims: subscribable, and one of them is a required claim.
    if (isOffered(entry, added)) result.add(entry.code);
  }
  return [...result].sort();
}

/** The notifications a role row subscribes to, in words, for the roles table. */
export function subscriptionLabels(codes, catalogue) {
  const byCode = new Map((catalogue ?? []).map((entry) => [entry.code, entry]));
  return (codes ?? []).map((code) => byCode.get(code)?.label ?? code);
}
